// The sky's sideways motion as pure math, tested without a DOM: the drift's
// speed eases toward its target, each depth layer moves by its own share of
// the drift and of the board's pan, and the wrapping field is drawn as a
// window of tiles around the view.

/** Exponential approach toward a target; tauMs is the time constant. */
export function approach(value: number, target: number, dtMs: number, tauMs: number): number {
  if (tauMs <= 0) return target;
  return target + (value - target) * Math.exp(-dtMs / tauMs);
}

/**
 * The drift speed after dtMs: it gathers over easeInMs and comes to a stop
 * over settleMs (about 98% of the way in that time), and snaps when it is
 * too slow to see.
 */
export function driftVelocity(velocity: number, target: number, dtMs: number, settleMs: number, easeInMs: number): number {
  const gathering = Math.abs(target) > Math.abs(velocity);
  const next = approach(velocity, target, dtMs, (gathering ? easeInMs : settleMs) / 4);
  return Math.abs(next - target) < 0.05 ? target : next;
}

/** Signed drift speed in sky units per second: positive moves the stars to the left. */
export function driftTarget(speed: number, direction: 'left' | 'right'): number {
  return direction === 'left' ? speed : -speed;
}

/** One frame of layer offsets (sky units): the drift's share plus the pan's share. */
export function stepOffsets(
  offsets: readonly number[],
  velocity: number,
  dtMs: number,
  driftFactors: readonly number[],
  panDelta: number,
  panFactors: readonly number[],
): number[] {
  return offsets.map((o, i) => o + velocity * (driftFactors[i] ?? 1) * (dtMs / 1000) + panDelta * (panFactors[i] ?? 0));
}

export function wrapIndex(k: number, n: number): number {
  return ((k % n) + n) % n;
}

export function wrapCoord(x: number, width: number): number {
  return ((x % width) + width) % width;
}

/** The first tile to draw for a layer at this offset: one spare tile on each side of the view. */
export function firstTile(offset: number, tileWidth: number): number {
  return Math.floor(offset / tileWidth) - 1;
}

/**
 * How many tiles cover a view this wide (sky units) from firstTile, with a
 * whole spare tile on each side, so the window may slide a frame late
 * without a gap showing.
 */
export function tileSpan(visibleWidth: number, tileWidth: number): number {
  return Math.ceil(visibleWidth / tileWidth) + 3;
}

/**
 * Where a field point shows on screen in pixels, or null when it is off
 * screen. halfTop is the field y at the top of the view (0 for play, 1000
 * for home).
 */
export function fieldToScreen(
  x: number,
  y: number,
  offset: number,
  halfTop: number,
  view: { width: number; height: number; scale: number },
  fieldWidth: number,
): { x: number; y: number } | null {
  const sx = wrapCoord(x - offset, fieldWidth) * view.scale;
  const sy = (y - halfTop) * view.scale;
  if (sx > view.width || sy < 0 || sy > view.height) return null;
  return { x: sx, y: sy };
}
